import { useEffect, useState } from "react";
import styled from "styled-components";
import CustomTextCard from "../../../common/components/card/CustomTextCard";
import CustomGraphCard, { weekdayButtonSectionValue } from "../../../common/components/card/CustomGraphCard";
import { Divider, Padding, SizedBox } from "../../../common/components/CommonBaseComponents";
import { useNavigate } from "react-router-dom";
import { CustomTable, LinkTd } from "../../../common/components/table/CustomTable";
import { T3Text } from "../../../common/components/text/CustomText";
import Translate from "../../../common/translate/Translate";
import RankCard from "../components/RankCard";
import { useDevice } from "../../../common/hooks/useDevice";
import { useWindowSize } from "../../../common/hooks/useWindowSize";
import RevenueService from "../../../service/RevenueService";

const WebTextCardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 17px;
    height: 170px;
`;

const WebGraphGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 17px;
    height: 426px;
`;

/**
 * dateRange 값("Today", "7days", "30days", "Custom")을 API용 startDate/endDate(yyyy-MM-dd)로 변환
 */
const getDateParams = (dateRange) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const endDate = fmt(today);
    let start = new Date(today);

    if (dateRange === "Today") {
        // 어제 ~ 오늘 (기본값)
        start.setDate(start.getDate() - 1);
    } else if (dateRange === "7days") {
        start.setDate(start.getDate() - 7);
    } else if (dateRange === "30days") {
        start.setDate(start.getDate() - 30);
    } else {
        start.setDate(start.getDate() - 7);
    }
    return { startDate: fmt(start), endDate };
};

function StatisticalAnalysisRevenueTabContent({ dateRange }) {
    const service = new RevenueService();
    const navigation = useNavigate();
    const { width } = useWindowSize();
    const currentDevice = useDevice();

    const [analysis, setAnalysis] = useState(null);
    const [graphDailyData, setGraphDailyData] = useState([]);
    const [shopList, setShopList] = useState([]);
    const [totalRevenueMainText, setTotalRevenueMainText] = useState("฿0");

    const fetchData = (dateRangeValue) => {
        const { startDate, endDate } = getDateParams(dateRangeValue);

        // 매출 분석 (spec 04.01.05.07: shop별 ranking 리스트)
        service
            .getRevenueAnalysis({ startDate, endDate })
            .then((result) => {
                const list = result || [];
                const totals = list.reduce(
                    (acc, item) => ({
                        total: acc.total + (item.total || 0),
                        washer: acc.washer + (item.washer || 0),
                        dryer: acc.dryer + (item.dryer || 0),
                        combo: acc.combo + (item.combo || 0),
                    }),
                    { total: 0, washer: 0, dryer: 0, combo: 0 }
                );
                setAnalysis(totals);
            })
            .catch((error) => {
                console.error("Error fetching revenue analysis:", error);
            });

        // 일별 매출 그래프 (spec 04.01.05.01: day, total, washer, dryer, combo)
        service
            .getRevenueDay({ startDate, endDate })
            .then((result) => {
                const graphData = (result || []).map((item) => ({
                    name: item.day,
                    Washer: item.washer,
                    Dryer: item.dryer,
                    Combo: item.combo,
                }));
                setGraphDailyData(graphData);
                const total = (result || []).reduce((sum, item) => sum + (item.total || 0), 0);
                setTotalRevenueMainText(`฿${total.toLocaleString()}`);
            })
            .catch((error) => {
                console.error("Error fetching revenue day:", error);
            });

        // 매장별 매출 목록 (spec 04.01.05.02: 플랫 리스트)
        service
            .getRevenueShop({ startDate, endDate })
            .then((result) => {
                const shops = (result || []).map((item, index) => ({
                    number: String(index + 1),
                    shopCode: item.shopCode,
                    shopName: item.shopName,
                    total: item.total,
                    washer: item.washer,
                    dryer: item.dryer,
                    combo: item.combo,
                }));
                setShopList(shops);
            })
            .catch((error) => {
                console.error("Error fetching revenue shop list:", error);
            });
    };

    useEffect(() => {
        fetchData(dateRange || "7days");
    }, [dateRange]);

    const tableHeaders = [
        {
            key: "number",
            label: Translate("common.number"),
        },
        {
            key: "shopCode",
            label: Translate("store.shopCode"),
            render: (row) => (
                <LinkTd
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigation(`/shops/${row.shopCode}`);
                    }}
                >
                    {row.shopCode}
                </LinkTd>
            ),
        },
        {
            key: "shopName",
            label: Translate("store.shopName"),
        },
        {
            key: "total",
            label: `${Translate("common.total")}(THB)`,
            render: (row) => <>฿{row.total?.toLocaleString()}</>,
        },
        {
            key: "washer",
            label: `${Translate("common.washer")}(THB)`,
            render: (row) => <>฿{row.washer?.toLocaleString()}</>,
        },
        {
            key: "dryer",
            label: `${Translate("common.dryer")}(THB)`,
            render: (row) => <>฿{row.dryer?.toLocaleString()}</>,
        },
        {
            key: "combo",
            label: `${Translate("common.combo")}(THB)`,
            render: (row) => <>฿{row.combo?.toLocaleString()}</>,
        },
    ];

    const totalRevenueCard = (
        <CustomTextCard
            icon={"bank_note"}
            title={Translate("dashboard.totalRevenue")}
            mainContent={analysis ? `฿${Number(analysis.total || 0).toLocaleString()}` : "฿0"}
            hasIcon={true}
        />
    );
    const refundCard = (
        <CustomTextCard icon={"card_minus"} title={Translate("revenue.refund")} mainContent={"฿0"} hasIcon={true} />
    );
    const dailyRevenueAverageCard = (
        <CustomTextCard
            icon={"bar_down"}
            title={Translate("dashboard.dailyRevenueAverage")}
            mainContent={"฿0"}
            hasIcon={true}
        />
    );
    const revenueByDateAndWeekdaysCard = (
        <CustomGraphCard
            dailyData={graphDailyData}
            weeklyData={graphDailyData}
            monthlyData={graphDailyData}
            title={"Revenue by Date & Weekdays"}
            mainText={totalRevenueMainText}
            hasLegend={false}
            hasBarLabel={true}
            buttonSectionValue={weekdayButtonSectionValue}
        />
    );
    const rankCard = <RankCard />;
    const table = (
        <>
            <SizedBox height={"16px"} width={"100%"} />
            <Padding padding={"0 21px"}>
                <T3Text>{Translate("revenue.revenueByShops")}</T3Text>
            </Padding>
            <Divider thickness="0" marginTop="12px" />
            <CustomTable headers={tableHeaders} data={shopList} />
        </>
    );

    if (currentDevice === "mobile") {
        return (
            <>
                {totalRevenueCard}
                <SizedBox height={"17px"} />
                {refundCard}
                <SizedBox height={"17px"} />
                {dailyRevenueAverageCard}
                <SizedBox height={"17px"} />
                {revenueByDateAndWeekdaysCard}
                <SizedBox height={"17px"} />
                {rankCard}
                {table}
            </>
        );
    } else if (width <= 1620) {
        return (
            <>
                <WebTextCardGrid>
                    {totalRevenueCard}
                    {refundCard}
                    {dailyRevenueAverageCard}
                </WebTextCardGrid>
                <SizedBox height={"17px"} />
                {revenueByDateAndWeekdaysCard}
                <SizedBox height={"17px"} />
                {rankCard}
                {table}
            </>
        );
    }

    return (
        <>
            <WebTextCardGrid>
                {totalRevenueCard}
                {refundCard}
                {dailyRevenueAverageCard}
            </WebTextCardGrid>
            <SizedBox height={"17px"} />
            <WebGraphGrid>
                {revenueByDateAndWeekdaysCard}
                {rankCard}
            </WebGraphGrid>
            {table}
        </>
    );
}

export default StatisticalAnalysisRevenueTabContent;
